'use client';

import { useCallback, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { MoreVerticalCircle01Icon } from '@terra/ui-icons';
import type { KnowledgeSyncProviderType } from '@terra/shared-contracts-knowledge-sync';
import { IconButton } from '../../../components/icon-button';
import { Menu } from '../../../components/menu';
import { BodyStrong, MetaDefault } from '../../../components/typography';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { getKnowledgeSourceProviderDisplay } from '../knowledgeSourceDisplay';
import {
  formatKnowledgeSourceScopeSummary,
  getKnowledgeSourceScopeCounts,
  getKnowledgeSourceScopeSummaryKeys,
  type KnowledgeSourceScope,
} from './knowledgeSourceScopes';

const PROVIDER_LOGO_SIZE = 32;

export type KnowledgeSourceCardProps = {
  provider: KnowledgeSyncProviderType;
  scopes: KnowledgeSourceScope[];
  syncedAtLabel: string;
  onEdit: (provider: KnowledgeSyncProviderType) => void;
  onRemove: (provider: KnowledgeSyncProviderType) => void;
  indexedDocumentCount?: number;
  isReadOnly?: boolean;
  providerLogo?: ReactNode;
};

export const KnowledgeSourceCard = ({
  provider,
  scopes,
  syncedAtLabel,
  onEdit,
  onRemove,
  indexedDocumentCount = 0,
  isReadOnly = false,
  providerLogo,
}: KnowledgeSourceCardProps) => {
  const t = useTranslations('designSystem.documentsReports.knowledgeSources');
  const tCommon = useTranslations('designSystem.common');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const { label: providerLabel, Icon: ProviderIcon } =
    getKnowledgeSourceProviderDisplay({ provider });
  const scopeSummary = formatKnowledgeSourceScopeSummary({
    counts: getKnowledgeSourceScopeCounts({ provider, scopes }),
    keys: getKnowledgeSourceScopeSummaryKeys(provider),
    formatCount: ({ key, count }) => t(key, { count }),
    joinParts: ({ containers, items }) =>
      t('scopeSummary.containersAndItems', { containers, items }),
  });
  const handleEdit = useCallback(() => {
    onEdit(provider);
  }, [onEdit, provider]);
  const handleRemoveClick = useCallback(() => {
    setRemoveDialogOpen(true);
  }, []);
  const handleRemoveConfirm = useCallback(() => {
    onRemove(provider);
    setRemoveDialogOpen(false);
  }, [onRemove, provider]);
  const handleRemoveDialogOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      setRemoveDialogOpen(open);
    },
    [],
  );

  return (
    <>
      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        py={3}
        px={4}
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        borderRadius="lg"
        bg="bg.neutral.light"
      >
        <HStack alignItems="center" gap={2} flex={1} minW={0}>
          {providerLogo ?? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              w={`${PROVIDER_LOGO_SIZE}px`}
              h={`${PROVIDER_LOGO_SIZE}px`}
              color="fg.neutral.default"
            >
              <ProviderIcon size={20} />
            </Box>
          )}
          <VStack alignItems="flex-start" gap={0} minW={0}>
            <BodyStrong color="fg.neutral.default">{providerLabel}</BodyStrong>
            <MetaDefault color="fg.neutral.muted">
              {t('selectionDescription', {
                summary: scopeSummary,
                time: syncedAtLabel,
              })}
            </MetaDefault>
          </VStack>
        </HStack>
        {isReadOnly ? null : (
          <Menu.Root
            lazyMount
            unmountOnExit
            positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
          >
            <Menu.Trigger asChild>
              <IconButton
                aria-label={tCommon('manage')}
                variant="outline"
                size="sm"
                flexShrink={0}
              >
                <MoreVerticalCircle01Icon size={16} />
              </IconButton>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content w="180px">
                <Menu.Item value="manage-content" onClick={handleEdit}>
                  {t('manageContent')}
                </Menu.Item>
                <Menu.Item value="remove-from-app" onClick={handleRemoveClick}>
                  {t('removeFromApp')}
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        )}
      </HStack>
      <ConfirmationDialog
        isOpen={removeDialogOpen}
        onOpenChange={handleRemoveDialogOpenChange}
        onConfirm={handleRemoveConfirm}
        variant="destructive"
        title={t('removeDialog.title')}
        description={t('removeDialog.description', {
          count: indexedDocumentCount,
        })}
        confirmText={t('removeDialog.remove')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
};
